/**
 * Multilingual Press Zone - Admin Panel Entry Point
 *
 * Main SPA entry file using Press Zone design system.
 *
 * @package MultilingualPressZone
 * @since 1.0.0
 */

import './styles/main.scss';
import { el, clear, __ } from './utils/dom.js';
import Toast from './components/Toast.js';
import logger from './utils/logger.js';

/**
 * Sidebar Component
 *
 * @param {Object} props - { items, activeId, onNavigate }
 * @return {HTMLElement}
 */
function Sidebar({ items, activeId, onNavigate }) {
    return el('aside', { class: 'presszone-multilingual-sidebar' },
        el('div', { class: 'presszone-multilingual-sidebar__header' },
            el('h2', { class: 'presszone-multilingual-sidebar__title' }, __('Multilingual Zone', 'multilingual-press-zone')),
            el('span', { class: 'presszone-multilingual-sidebar__version' },
                window.multilingualPressZone?.version || 'v1.0.0'
            )
        ),
        el('nav', { class: 'presszone-multilingual-sidebar__nav' },
            ...items.map(item => el('a', {
                class: `presszone-multilingual-sidebar__link ${activeId === item.id ? 'presszone-multilingual-sidebar__link--active' : ''}`,
                'data-id': item.id,
                href: `#/${item.id}`,
                onclick: (e) => {
                    e.preventDefault();
                    // Close mobile sidebar when navigating
                    document.body.classList.remove('presszone-multilingual-sidebar-open');
                    onNavigate(item.id);
                }
            },
                el('span', { class: 'presszone-multilingual-sidebar__icon' }, item.icon),
                el('span', { class: 'presszone-multilingual-sidebar__label' }, item.label)
            ))
        )
    );
}

/**
 * Mobile Hamburger Button
 *
 * @return {HTMLElement}
 */
function HamburgerButton() {
    return el('button', {
        class: 'presszone-multilingual-hamburger',
        'aria-label': __('Toggle Menu', 'multilingual-press-zone'),
        onclick: () => document.body.classList.toggle('presszone-multilingual-sidebar-open')
    },
        el('span', { class: 'presszone-multilingual-hamburger__bar' }),
        el('span', { class: 'presszone-multilingual-hamburger__bar' }),
        el('span', { class: 'presszone-multilingual-hamburger__bar' })
    );
}

/**
 * Mobile Sidebar Overlay
 *
 * @return {HTMLElement}
 */
function SidebarOverlay() {
    return el('div', {
        class: 'presszone-multilingual-sidebar-overlay',
        onclick: () => document.body.classList.remove('presszone-multilingual-sidebar-open')
    });
}

// Import pages
import DashboardPage from './pages/dashboard.js';
import LicensingPage from './pages/licensing.js';
// Import other pages dynamically to reduce bundle size if needed, 
// but for now we import normally or use dynamic import in router.


/**
 * Admin Panel Application Class
 */
class MultilingualPressZoneAdmin {
    constructor() {
        this.version = '1.0.0';
        this.initialized = false;
        this.currentPage = null;
        this.routerRoot = document.getElementById('mpz-admin-root');

        console.log('Multilingual Press Zone Admin Panel - Version', this.version);
    }

    /**
     * Initialize the admin panel
     */
    init() {
        if (this.initialized) {
            console.warn('Admin panel already initialized');
            return;
        }

        try {
            // Locate the root element if not found in constructor (DOM might not be ready then)
            this.routerRoot = document.getElementById('mpz-admin-root');

            if (!this.routerRoot) {
                Toast.error(__('Admin panel container not found. Please reload the page.', 'multilingual-press-zone'));
                return;
            }

            // License gate: show onboarding if no active license
            if (!window.multilingualPressZone?.license_active) {
                this.renderOnboarding();
                this.initialized = true;
                return;
            }

            // Render the app shell with sidebar
            this.renderAppShell();

            this.setupEventListeners();
            this.handleRoute(); // Initial route
            this.initialized = true;

            console.log('Admin panel initialized successfully');

            // Dispatch ready event
            document.dispatchEvent(new CustomEvent('multilingual-press-zone:ready', {
                detail: { version: this.version }
            }));
        } catch (error) {
            Toast.error(error?.message || __('Failed to initialize admin panel', 'multilingual-press-zone'));
        }
    }

    /**
     * Render the application shell with sidebar
     */
    renderAppShell() {
        // Clear root
        clear(this.routerRoot);

        // Navigation items (Onboarding is NOT in sidebar)
        const navItems = [
            { id: 'dashboard', label: __('Dashboard', 'multilingual-press-zone'), icon: '📊' },
            { id: 'languages', label: __('Languages', 'multilingual-press-zone'), icon: '🌐' },
            { id: 'translations', label: __('Translations', 'multilingual-press-zone'), icon: '📝' },
            { id: 'settings', label: __('Settings', 'multilingual-press-zone'), icon: '⚙️' },
            { id: 'licensing', label: __('Licensing', 'multilingual-press-zone'), icon: '🔑' },
            { id: 'migration', label: __('Migration', 'multilingual-press-zone'), icon: '🔄' },
        ];

        // Create admin container wrapper
        const adminContainer = el('div', { class: 'presszone-multilingual-admin' });

        // Create sidebar
        const hash = window.location.hash.replace(/^#\//, '') || '';
        const activeRoute = hash.split('/')[0] || 'dashboard';

        this.sidebar = Sidebar({
            items: navItems,
            activeId: activeRoute,
            onNavigate: (id) => {
                window.location.hash = `#/${id}`;
            }
        });

        // Create main content area
        this.mainContent = el('main', {
            class: 'presszone-multilingual-admin-main',
            id: 'presszone-multilingual-content'
        });

        // Global modal container
        const modalContainer = el('div', { id: 'presszone-multilingual-modal-container' });

        // Mobile controls
        const hamburger = HamburgerButton();
        const overlay = SidebarOverlay();

        // Mount everything to admin container
        adminContainer.appendChild(hamburger);
        adminContainer.appendChild(overlay);
        adminContainer.appendChild(this.sidebar);
        adminContainer.appendChild(this.mainContent);
        adminContainer.appendChild(modalContainer);

        // Mount admin container to root
        this.routerRoot.appendChild(adminContainer);
    }

    /**
     * Setup global event listeners
     */
    setupEventListeners() {
        // DOM ready handler
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => {
                this.onDOMReady();
            });
        } else {
            this.onDOMReady();
        }

        // Hash change listener for routing
        window.addEventListener('hashchange', () => {
            this.handleRoute();
        });
    }

    /**
     * DOM ready callback
     */
    onDOMReady() {
        console.log('DOM ready - checking router root');
        if (!this.routerRoot) {
            this.routerRoot = document.getElementById('mpz-admin-root');
        }
    }

    /**
    /**
     * Update sidebar active state
     */
    updateSidebarActiveState(route) {
        if (!this.sidebar) return;
        const links = this.sidebar.querySelectorAll('.presszone-multilingual-sidebar__link');
        links.forEach(link => {
            const linkId = link.getAttribute('data-id');
            if (linkId === route) {
                link.classList.add('presszone-multilingual-sidebar__link--active');
            } else {
                link.classList.remove('presszone-multilingual-sidebar__link--active');
            }
        });
    }


    /** Handle routing based on URL hash
     */
    async handleRoute() {
        if (!this.mainContent) {
            console.warn('Main content element not ready, skipping route');
            return;
        }

        const hash = window.location.hash.replace(/^#\//, '') || ''; // Remove #/ prefix
        // Handle routes like 'translations/themes', 'languages', etc.
        // Default to dashboard if empty
        const parts = hash.split('/');
        const route = parts[0] || 'dashboard';
        const subRoute = parts[1] || null;

        // Analytics was merged into Dashboard; keep a soft redirect for old URLs.
        if (route === 'analytics') {
            window.location.hash = '#/dashboard';
            return;
        }

        console.log(`Navigating to route: ${route}${subRoute ? '/' + subRoute : ''}`);

        // If we're already on the translations page and only the sub-tab changed,
        // just switch the tab without re-rendering the entire page.
        if (route === 'translations' && this.currentPage && this.currentPage.isTranslationsPage) {
            const validTabs = ['themes', 'pages', 'posts', 'plugins'];
            const tab = validTabs.includes(subRoute) ? subRoute : 'themes';
            if (tab !== this.currentPage.activeTab) {
                this.currentPage.switchTab(tab, { updateHash: false });
            }
            return;
        }

        // Same for settings — don't re-render if only the sub-tab changed
        if (route === 'settings' && this.currentPage && this.currentPage.activeTab) {
            const validSettingsTabs = ['general', 'cache', 'performance', 'advanced'];
            const settingsTab = validSettingsTabs.includes(subRoute) ? subRoute : null;
            if (settingsTab && settingsTab !== this.currentPage.activeTab) {
                this.currentPage.activeTab = settingsTab;
                this.currentPage.renderTabContent();
            }
            return;
        }

        // Update sidebar active state
        this.updateSidebarActiveState(route);

        // Cleanup current page
        if (this.currentPage && typeof this.currentPage.destroy === 'function') {
            this.currentPage.destroy();
        }

        // Clear main content area (not the entire root)
        clear(this.mainContent);

        try {
            switch (route) {
                case 'dashboard':
                case '':
                    await this.renderDashboard();
                    break;
                case 'languages':
                    await this.renderLanguages();
                    break;
                case 'translations':
                    await this.renderTranslations(subRoute);
                    break;
                case 'settings':
                    await this.renderSettings(subRoute);
                    break;
                case 'licensing':
                    await this.renderLicensing();
                    break;
                case 'migration':
                    await this.renderMigration();
                    break;
                case 'onboarding':
                    await this.renderOnboarding();
                    break;
                default:
                    console.warn(`Unknown route: ${route}`);
                    await this.renderDashboard(); // Fallback
            }

            // Update active menu item in WP Admin sidebar
            this.updateAdminMenuHighlight(route);

        } catch (error) {
            Toast.error(error?.message || __('Failed to load page.', 'multilingual-press-zone'));
            this.routerRoot.innerHTML = `<div class="notice notice-error"><p>${__('Failed to load page.', 'multilingual-press-zone')}</p></div>`;
        }
    }

    /**
     * Update the WP Admin sidebar highlight to match the current pseudo-page
     * @param {string} route 
     */
    updateAdminMenuHighlight(route) {
        // Fix for WP admin menu highlighting
        // WP highlights based on the `page` query param.
        // For submenus with custom slugs like `admin.php?page=multilingual-press-zone#/languages`, 
        // we might need to manually set the active class.

        const adminMenu = document.getElementById('adminmenu');
        if (!adminMenu) return;

        // Remove current current
        const currentItems = adminMenu.querySelectorAll('.current');
        currentItems.forEach(el => el.classList.remove('current'));

        // Construct the href we are looking for
        // Our MenuController registers slugs like: admin.php?page=multilingual-press-zone#/settings
        // BUT wait, add_submenu_page with 'admin.php?page=...' might output a full href.
        // Let's assume the href in the DOM matches what we passed.

        let selector = '';
        if (route === 'dashboard' || route === '') {
            selector = 'a[href="admin.php?page=multilingual-press-zone"]';
            // Also the subpage duplicate of dashboard
            // selector could also be 'a[href$="page=multilingual-press-zone"]'
        } else {
            // The slug registered was 'admin.php?page=multilingual-press-zone#/' + route
            selector = `a[href*="page=multilingual-press-zone#/${route}"]`;
        }

        const link = adminMenu.querySelector(selector);

        if (link) {
            const li = link.closest('li');
            if (li) {
                li.classList.add('current');
            }
            // Ensure parent is open/active
            const parentLi = link.closest('li.toplevel_page_multilingual-press-zone');
            if (parentLi) {
                parentLi.classList.add('wp-has-current-submenu', 'wp-menu-open');
                parentLi.classList.remove('wp-not-current-submenu');
                const parentLink = parentLi.querySelector('a.toplevel_page_multilingual-press-zone');
                if (parentLink) parentLink.classList.add('wp-has-current-submenu', 'wp-menu-open');
            }
        }
    }

    /**
     * Render Dashboard page
     */
    async renderDashboard() {
        console.log('Loading Dashboard page...');
        const { default: DashboardPage } = await import(/* webpackChunkName: "dashboard" */ './pages/dashboard.js');
        const container = document.createElement('div');
        container.id = 'mpz-dashboard-root';
        this.mainContent.appendChild(container);

        const page = new DashboardPage(container);
        page.render();

        this.currentPage = page;
    }

    /**
     * Render Languages page
     */
    async renderLanguages() {
        console.log('Loading Languages page...');
        const { default: LanguagesPage } = await import(/* webpackChunkName: "languages" */ './pages/languages.js');
        const container = document.createElement('div');
        container.id = 'mpz-languages-root';
        this.mainContent.appendChild(container);

        const page = new LanguagesPage(container);
        await page.render();

        this.currentPage = page;
    }

    /**
     * Render Translations page
     *
     * @param {string|null} initialTab - Initial tab to activate (themes, pages, posts, plugins)
     */
    async renderTranslations(initialTab) {
        console.log('Loading Translations page...');
        const { default: TranslationsPage } = await import(/* webpackChunkName: "translations" */ './pages/translations.js');
        const container = document.createElement('div');
        container.id = 'mpz-translations-root';
        this.mainContent.appendChild(container);

        const page = new TranslationsPage(container, initialTab);
        await page.render();

        this.currentPage = page;
    }

    /**
     * Render Settings page
     */
    async renderSettings(subRoute) {
        console.log('Loading Settings page...');
        const { default: SettingsPage } = await import(/* webpackChunkName: "settings" */ './pages/settings.js');
        const container = document.createElement('div');
        container.id = 'mpz-settings-root';
        this.mainContent.appendChild(container);

        const page = new SettingsPage(container, subRoute || '');
        await page.render();

        this.currentPage = page;
    }

    /**
     * Render Licensing page
     */
    async renderLicensing() {
        console.log('Loading Licensing page...');
        const { default: LicensingPage } = await import(/* webpackChunkName: "licensing" */ './pages/licensing.js');
        const container = document.createElement('div');
        container.id = 'mpz-licensing-root';
        this.mainContent.appendChild(container);

        const page = new LicensingPage(container);
        await page.render();

        this.currentPage = page;
    }

    /**
     * Render Analytics page
     */
    async renderAnalytics() {
        console.log('Loading Analytics page...');
        const { default: AnalyticsPage } = await import(/* webpackChunkName: "analytics" */ './pages/analytics.js');
        const container = document.createElement('div');
        container.id = 'mpz-analytics-root';
        this.mainContent.appendChild(container);

        const page = new AnalyticsPage(container);
        await page.render();

        this.currentPage = page;
    }

    /**
     * Render Migration page
     */
    async renderMigration() {
        console.log('Loading Migration page...');
        const { default: MigrationPage } = await import(/* webpackChunkName: "migration" */ './pages/migration.js');
        const container = document.createElement('div');
        container.id = 'mpz-migration-root';
        this.mainContent.appendChild(container);

        const page = new MigrationPage(container);
        await page.render();

        this.currentPage = page;
    }

    /**
     * Render Onboarding Wizard
     */
    async renderOnboarding() {
        console.log('Loading Onboarding Wizard...');
        const { default: OnboardingPage } = await import(/* webpackChunkName: "onboarding" */ './pages/onboarding.js');
        const target = this.mainContent || this.routerRoot;
        clear(target);
        const container = document.createElement('div');
        container.id = 'mpz-onboarding-root';
        target.appendChild(container);

        const page = new OnboardingPage(container);
        page.render();

        this.currentPage = page;
    }

    /**
     * Get admin panel version
     *
     * @return {string} Version number
     */
    getVersion() {
        return this.version;
    }
}

// Initialize admin panel
const adminPanel = new MultilingualPressZoneAdmin();

// Auto-initialize when script loads
adminPanel.init();

// Export pages for global access
window.MultilingualPressZone = {
    adminPanel,
    LicensingPage
};

// Export for module usage
export default adminPanel;
export { LicensingPage };
